import { GAME_FONT_FAMILY } from '@shared/constants';
import { ALL_SKILL_IDS, SKILL_DEFS } from '@shared/data/skills';
import {
  createSelect,
  createTextInput,
  createButton,
  ensureFormControlsStyle,
  type SelectControl,
  type TextInputControl,
} from '../../ui/formControls';

/**
 * Ventanita de DEBUG (sólo admin) para dar experiencia de habilidad y probar el
 * sistema de Skills sin farmear. Panel DOM (formControls) fijo arriba a la
 * derecha, toggleado desde el botón 🎓 de la barra de dev. Envía
 * `DEBUG_GRANT_SKILL_XP` (el server valida `isAdmin`). Ver docs/SKILLS_SYSTEM.md.
 */

export interface DebugSkillXpCallbacks {
  /** Jugadores en la sala para el select (default: uno mismo primero). */
  getPlayers: () => Array<{ id: string; name: string; isSelf: boolean }>;
  /** Dispara el envío al server. targetId '' = uno mismo. */
  onGrant: (targetSessionId: string, skillId: string, amount: number) => void;
}

export class DebugSkillXpPanel {
  private root: HTMLDivElement;
  private userSelect: SelectControl;
  private skillSelect: SelectControl;
  private xpInput: TextInputControl;
  private open = false;

  constructor(private callbacks: DebugSkillXpCallbacks) {
    ensureFormControlsStyle();

    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed',
      top: '76px',
      right: '12px',
      width: '236px',
      padding: '12px',
      background: 'rgba(20, 16, 10, 0.96)',
      border: '2px solid #caa24a',
      borderRadius: '6px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.55)',
      zIndex: '10001',
      display: 'none',
      fontFamily: GAME_FONT_FAMILY,
      color: '#e8e0cf',
    } as CSSStyleDeclaration);

    // Las teclas tipeadas dentro del panel NO deben llegar al juego (movimiento /
    // slots de hechizo). Cortar la propagación al window (donde escucha Phaser).
    this.root.addEventListener('keydown', (e) => e.stopPropagation());

    const title = document.createElement('div');
    title.textContent = 'Dar XP (debug)';
    Object.assign(title.style, {
      fontSize: '15px',
      color: '#f0d68c',
      marginBottom: '10px',
      textAlign: 'center',
    } as CSSStyleDeclaration);
    this.root.appendChild(title);

    this.root.appendChild(this.field('Usuario'));
    this.userSelect = createSelect({ options: [] });
    this.root.appendChild(this.userSelect.wrapper);

    this.root.appendChild(this.field('Habilidad'));
    this.skillSelect = createSelect({
      options: ALL_SKILL_IDS.map((id) => ({ value: id, label: SKILL_DEFS[id].name })),
    });
    this.root.appendChild(this.skillSelect.wrapper);

    this.root.appendChild(this.field('Experiencia'));
    this.xpInput = createTextInput({ value: '1000', placeholder: 'ej. 1000' });
    this.xpInput.input.inputMode = 'numeric';
    this.root.appendChild(this.xpInput.wrapper);

    const btn = createButton('Dar experiencia', { fullWidth: true });
    btn.style.marginTop = '12px';
    btn.onclick = () => this.grant();
    this.root.appendChild(btn);

    document.body.appendChild(this.root);
  }

  /** Etiqueta chica para un campo. */
  private field(text: string): HTMLDivElement {
    const el = document.createElement('div');
    el.textContent = text;
    Object.assign(el.style, {
      fontSize: '12px',
      color: '#caa24a',
      margin: '8px 0 3px',
    } as CSSStyleDeclaration);
    return el;
  }

  /** Refresca el select de usuarios (default: uno mismo). */
  private refreshUsers(): void {
    const players = this.callbacks.getPlayers();
    // Uno mismo primero.
    players.sort((a, b) => (a.isSelf === b.isSelf ? 0 : a.isSelf ? -1 : 1));
    this.userSelect.setOptions(
      players.map((p) => ({ value: p.id, label: p.isSelf ? `${p.name} (vos)` : p.name }))
    );
    const self = players.find((p) => p.isSelf);
    if (self) this.userSelect.select.value = self.id;
  }

  private grant(): void {
    const targetId = this.userSelect.select.value;
    const skillId = this.skillSelect.select.value;
    const amount = parseInt(this.xpInput.input.value, 10);
    if (!skillId || !Number.isFinite(amount) || amount <= 0) return;
    this.callbacks.onGrant(targetId, skillId, amount);
  }

  public toggle(): void {
    this.open = !this.open;
    if (this.open) this.refreshUsers();
    this.root.style.display = this.open ? 'block' : 'none';
  }

  public isOpen(): boolean {
    return this.open;
  }

  public destroy(): void {
    this.root.remove();
  }
}
